'use client';

import { useCallback, useEffect, useState } from 'react';

import useEmblaCarousel from 'embla-carousel-react';

import { Variant } from '../Variant';

import { NavigationButton } from './NavigationButton';
import { variantsCarouselStyles } from './VariantsCarousel.styles';

type TypeProps = {
  id: number;
  label: string;
  url: string;
  hasStock: boolean;
};

interface VariantsCarouselProps {
  types: TypeProps[];
  selectedId: number;
}

function VariantsCarousel(props: Readonly<VariantsCarouselProps>) {
  const { types, selectedId } = props;

  const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const variantsCarouselClassNames = variantsCarouselStyles();

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const checkScrollButtons = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    checkScrollButtons();
    emblaApi.on('select', checkScrollButtons);
    emblaApi.on('resize', checkScrollButtons);

    return () => {
      emblaApi.off('select', checkScrollButtons);
      emblaApi.off('resize', checkScrollButtons);
    };
  }, [emblaApi]);

  return (
    <div className="relative" data-testid="variants-carousel">
      {canScrollPrev && (
        <NavigationButton direction="left" onClick={scrollPrev} />
      )}
      <div className={variantsCarouselClassNames.base()}>
        <div className={variantsCarouselClassNames.viewport()} ref={emblaRef}>
          <div className={variantsCarouselClassNames.container()}>
            {types.map(({ id, label, url, hasStock }) => {
              const isActive = selectedId === id;
              const isDisabled = !hasStock && !isActive;

              return (
                <Variant
                  key={id}
                  label={label}
                  url={url}
                  isActive={isActive}
                  isDisabled={isDisabled}
                />
              );
            })}
          </div>
        </div>
      </div>
      {canScrollNext && (
        <NavigationButton direction="right" onClick={scrollNext} />
      )}
    </div>
  );
}

export default VariantsCarousel;
